import { Badge } from '@tablecheck/tablekit-react';
import { useTranslation } from 'react-i18next';

import {
  AppNoticeContent,
  DateText,
  Image,
  ImageWrapper,
  Link,
  TextMessage,
  TextTitle,
  TextWrapper,
  Title
} from 'components/appNotices/AppNotice.styles';
import {
  AppNoticeAPI,
  AppNoticeImageAndTextSectionAPI,
  AppNoticeImageSectionAPI
} from 'components/appNotices/types';
import {
  getBadgeVariantForAppNotice,
  getFormattedDate
} from 'components/appNotices/utils';

import { AppNoticeText } from './TextSection';

export function AppNoticeImage({
  element
}: {
  element: AppNoticeImageSectionAPI;
}): JSX.Element {
  const { image_urls: imageUrls, cta_link_url: linkUrl } = element;

  return (
    <ImageWrapper>
      <Link href={linkUrl || undefined} target="_blank" rel="noreferrer">
        <Image src={imageUrls.original} />
      </Link>
    </ImageWrapper>
  );
}

export function AppNoticeImageAndText({
  element
}: {
  element: AppNoticeImageAndTextSectionAPI;
}): JSX.Element {
  const {
    title_text: title,
    content_text: message,
    image_layout: imageLayout,
    image_urls: imageUrls
  } = element;

  return (
    <TextWrapper
      data-image-position={imageLayout}
      data-variant={!title ? 'untitled' : undefined}
    >
      {title ? <TextTitle>{title}</TextTitle> : null}
      <Image src={imageUrls.original} alt={title} />
      <TextMessage>
        <div
          dangerouslySetInnerHTML={{
            __html: message
          }}
        />
      </TextMessage>
    </TextWrapper>
  );
}

export function AppNotice({
  appNotice
}: {
  appNotice: AppNoticeAPI;
}): JSX.Element | null {
  const [t, { language }] = useTranslation();

  const {
    category,
    start_date: startDate,
    app_notice_sections: appNoticeSections
  } = appNotice;
  const isEventAppNotice = category === 'event';

  return (
    <AppNoticeContent>
      <Title>
        <Badge data-variant={getBadgeVariantForAppNotice(category)}>
          {t(`app_notice.category.${category}`)}
        </Badge>
        {!isEventAppNotice && startDate ? (
          <DateText>{getFormattedDate(startDate, language)}</DateText>
        ) : null}
      </Title>
      {(appNoticeSections || []).map((element) => {
        if (element.ui_type === 'text') {
          return <AppNoticeText key={element.id} element={element} />;
        }
        if (element.ui_type === 'image_and_text') {
          return <AppNoticeImageAndText key={element.id} element={element} />;
        }
        return <AppNoticeImage key={element.id} element={element} />;
      })}
    </AppNoticeContent>
  );
}
